import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { useLocalizedRouterServer } from "@ctv/shared-core/src";

import { useClientApi, useContentResource } from "@ctv/core";
import useUpload from "@ctv/core/api/useUpload";
import { useDispatchSnackbar } from "@ctv/shared/contexts/SnackbarContext";

import { usePreRegisterConfirm } from "../../_contexts/PreRegisterConfirmContext";
import { useRegisterContent } from "../../_contexts/RegisterContentContext";
import { useRequiredDocument } from "../../_contexts/RequiredDocumentContext";
import {
  DOWNLOAD_DOCUMENT_API,
  DownloadDocumentRequest,
  DownloadDocumentResponse,
  SAVE_AND_EXIT_API,
  SaveAndExitRequest,
  SaveAndExitResponse,
  UPLOAD_DOCUMENT_API,
  UploadDocumentRequest,
  UploadDocumentResponse,
} from "../../apis";
import { RegistrationFormValue } from "../../types";
import { handleProcessDataBeforeSubmit } from "../RegistrationPaymentMethod/helper";

export default function useRegistrationDocument() {
  const router = useLocalizedRouterServer();
  const openSnackbar = useDispatchSnackbar();

  const {
    documents: documentFormList,
    creditAssessmentDocuments: documentCAFormList,
  } = useRequiredDocument();
  const { ndaRequested, documents } = usePreRegisterConfirm()!;
  const { token, onRequestNDA } = useRegisterContent()!;
  const { CorporateRegistration: cr } = useContentResource();

  const [isRequestNDAVisible, setIsRequestNDAVisible] =
    useState<boolean>(false);

  const {
    getValues,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<RegistrationFormValue>();
  const [paymentMethod, country] = getValues(["paymentMethod", "country"]);

  const save = useClientApi<SaveAndExitResponse, SaveAndExitRequest>({
    domain: "management",
    method: "post",
    path: SAVE_AND_EXIT_API,
  });

  const upload = useUpload<UploadDocumentResponse, UploadDocumentRequest>({
    path: UPLOAD_DOCUMENT_API,
  });

  const download = useClientApi<
    DownloadDocumentResponse,
    DownloadDocumentRequest
  >({
    domain: "management",
    method: "post",
    path: DOWNLOAD_DOCUMENT_API,
  });

  async function handleUploadDocument(documentType: string, file: File) {
    const res = await upload(
      {
        country,
        documentType,
        paymentMethod,
        token,
      },
      file
    );

    if (res.success) return res.data;
    else throw res.error;
  }

  function handleOnClickSaveAndExit() {
    const transformData = handleProcessDataBeforeSubmit(getValues());
    const payload: SaveAndExitRequest = {
      ...transformData,
      token,
    };
    save(payload)
      .then((res) => {
        if (res.success) {
          router.push("/");
          window.scrollTo({ top: 0 });
        } else {
          throw new Error("Failed to save and exit");
        }
      })
      .catch(() => {
        openSnackbar({ message: cr.submitErrorMessage, variant: "alert" });
      });
  }

  function handleDownloadAgreementForm() {
    download({
      documentType: "INVOICE_AGREEMENT_FORM",
      token,
    })
      .then((res) => {
        if (res.success) {
          window.open(res.data.url, "_blank")?.focus();
        } else {
          openSnackbar({
            message: cr.failedDownloadErrorMessage,
            variant: "alert",
          });
        }
      })
      .catch(() =>
        openSnackbar({
          message: cr.failedDownloadErrorMessage,
          variant: "alert",
        })
      );
  }

  return {
    cr,
    isRequestNDAVisible,
    registrationData: {
      documents,
      ndaRequested,
      paymentMethod,
      documentFormList,
      documentCAFormList,
    },
    isFormSubmitting: isSubmitting,
    handleSubmit,
    onRequestNDA,
    handleUploadDocument,
    setIsRequestNDAVisible,
    handleOnClickSaveAndExit,
    handleDownloadAgreementForm,
  };
}
